import { useEffect, useState, type FC } from "react";
import type { RiskScoreModel } from "../../models/RiskScoreModel";
import styled from "styled-components";
import { theme } from "../../theme";
import TitleSmall from "../atoms/typography/TitleSmall";
import { HorizontalSpacer } from "../atoms/Spacers";
import { Row } from "react-grid-system";
import BodySmall from "../atoms/typography/BodySmall";
import dayjs from "dayjs";
import ClientManualRules from "../molecules/aml/ClientManualRules";
import Small from "../atoms/typography/Small";
import Subtitle from "../atoms/typography/Subtitle";
import Body from "../atoms/typography/Body";
import {
  getRiskLevel,
  getRiskLevelColor,
  getRiskScoreTitleAndDescription,
} from "../../utils/amlHelpers";
import { useTranslation } from "react-i18next";
import ClientRiskRuleDetailedActionComponent from "../molecules/aml/ClientRiskRuleDetailedActionComponent";
import { SlideOutHeight } from "../atoms/SlideOutHeight";
import {
  ChevronSmallDownIcon,
  ChevronSmallUpIcon,
} from "../atoms/icons/ChevronIcons";
import DetailsButton from "../atoms/buttons/DetailsButton";
import Subheading from "../atoms/typography/Subheading";

interface Props {
  riskScore: RiskScoreModel;
  clientIdentifer: string;
  refetchRiskScore?: () => void;
}

const OutlinedDiv = styled.div`
  border: 1px solid ${theme.colors.grey05};
  border-radius: 12px;
  padding: 32px;
`;

const DetailedContainer = styled.div`
  width: 50%;
  padding: 16px;
  background-color: ${theme.colors.grey02};
`;

const InfoRuleBoxescontainer = styled.div`
  width: 50%;
`;

const InforRuleBox = styled.div<{ selected?: boolean }>`
  padding: 16px;
  background-color: ${(props) =>
    props.selected ? theme.colors.grey02 : "transparent"};
  border-bottom: 1px solid ${theme.colors.grey05};
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    cursor: pointer;
    background-color: ${theme.colors.hover};
  }
`;

const ScoreContainer = styled.div`
  width: 70px;
  flex-shrink: 0;
`;

const ClientRiskScores: FC<Props> = ({
  riskScore,
  clientIdentifer,
  refetchRiskScore,
}) => {
  const { t } = useTranslation();
  const [selectedRule, setSelectedRule] = useState(
    riskScore.current?.rules?.[0]?.id ?? "",
  );
  const [displayWithoutScores, setDisplayWithoutScores] = useState(false);

  const riskAssessmentsWithScoring = riskScore.current?.rules?.filter(
    (x) => x.score !== 0,
  );
  const riskAssessmentsWithoutScoring = riskScore.current?.rules?.filter(
    (x) => x.score === 0,
  );

  useEffect(() => {
    setSelectedRule(riskAssessmentsWithScoring?.[0]?.id ?? "");
  }, [riskScore]);

  const selectedRuleObject = riskScore.current?.rules?.find(
    (r) => r.id === selectedRule,
  );

  return (
    <div>
      <OutlinedDiv>
        <TitleSmall color={theme.colors.primary}>
          {t("aml.riskScores")}
        </TitleSmall>
        <HorizontalSpacer spacing={2} />
        <div style={{ width: 400 }}>
          <Row nogutter justify="between">
            <BodySmall>{t("aml.latestRiskScoreChange")}</BodySmall>
            <BodySmall fontweight={500}>
              {dayjs(riskScore?.current?.created).format("YYYY-MM-DD, HH:mm")}
            </BodySmall>
          </Row>
          {riskScore?.current?.created !==
            riskScore?.current?.latestUpdatedAt && (
            <div>
              <HorizontalSpacer spacing={1} />
              <Row nogutter justify="between">
                <BodySmall>{t("aml.latestRun")}</BodySmall>
                <BodySmall fontweight={500}>
                  {dayjs(riskScore?.current?.latestUpdatedAt).format(
                    "YYYY-MM-DD, HH:mm",
                  )}
                </BodySmall>
              </Row>
            </div>
          )}
        </div>

        <HorizontalSpacer spacing={2} />
        <ClientManualRules clientIdentifier={clientIdentifer} />

        <HorizontalSpacer spacing={2} />
        <Row justify="between" nogutter align="center">
          <Subheading>{t("aml.results")}</Subheading>
          <div>
            <Small color={theme.colors.textSecondary}>
              <b>!</b> {t("aml.indicatesMissingDataResultingInExtraScore")}
            </Small>
            <Small color={theme.colors.textSecondary} textalign="right">
              <b>*</b> {t("aml.indicatesRuleWithException")}
            </Small>
          </div>
        </Row>

        <HorizontalSpacer spacing={1} />
        <div style={{ display: "flex" }}>
          <InfoRuleBoxescontainer>
            {riskAssessmentsWithScoring?.map((rs) => (
              <InforRuleBox
                key={rs.id}
                selected={selectedRule === rs.id}
                onClick={() => setSelectedRule(rs.id)}
              >
                <ScoreContainer>
                  <Subtitle
                    color={getRiskLevelColor(rs.score, rs.manualRiskScore)}
                    textalign="center"
                    fontWeight={700}
                  >
                    {rs.manualRiskScore ?? rs.score}
                    {rs?.ruleResult === "MISSING_DATA" && "!"}
                  </Subtitle>
                  <BodySmall
                    fontweight={600}
                    color={getRiskLevelColor(rs.score, rs.manualRiskScore)}
                    textalign="center"
                  >
                    {getRiskLevel(rs.score)}
                  </BodySmall>
                </ScoreContainer>

                <div>
                  <Body fontweight="500">
                    {t(getRiskScoreTitleAndDescription(rs.type).title)}
                  </Body>
                  <BodySmall color={theme.colors.textSecondary}>
                    {rs.description}
                  </BodySmall>
                  <HorizontalSpacer spacing={0.25} />
                  {rs.comments && (
                    <div>
                      {rs.comments?.items?.map((c) => (
                        <BodySmall>"{c.content}"</BodySmall>
                      ))}
                    </div>
                  )}
                  <Small color={theme.colors.textMuted}>
                    {rs.scoreImpactDescription}
                  </Small>
                </div>
              </InforRuleBox>
            ))}
            <SlideOutHeight isVisible={displayWithoutScores}>
              {riskAssessmentsWithoutScoring?.map((rs) => (
                <InforRuleBox
                  key={rs.id}
                  selected={selectedRule === rs.id}
                  onClick={() => setSelectedRule(rs.id)}
                >
                  <ScoreContainer>
                    <Subtitle
                      color={getRiskLevelColor(rs.score, rs.manualRiskScore)}
                      textalign="center"
                    >
                      {rs.manualRiskScore ?? rs.score}
                      {rs?.ruleResult === "MISSING_DATA" && "!"}
                    </Subtitle>
                    <BodySmall
                      fontweight={600}
                      color={getRiskLevelColor(rs.score, rs.manualRiskScore)}
                      textalign="center"
                    >
                      {getRiskLevel(rs.score)}
                    </BodySmall>
                  </ScoreContainer>

                  <div>
                    <Body fontweight="700">
                      {t(getRiskScoreTitleAndDescription(rs.type).title)}
                    </Body>
                    <BodySmall color={theme.colors.textSecondary}>
                      {rs.description}
                    </BodySmall>
                    <HorizontalSpacer spacing={0.25} />
                    {rs.comments && (
                      <div>
                        {rs.comments?.items?.map((c) => (
                          <BodySmall>"{c.content}"</BodySmall>
                        ))}
                      </div>
                    )}
                    <Small color={theme.colors.textMuted}>
                      {rs.scoreImpactDescription}
                    </Small>
                  </div>
                </InforRuleBox>
              ))}
            </SlideOutHeight>
            <Row
              nogutter
              justify="end"
              align="center"
              style={{ padding: "16px 8px" }}
            >
              <DetailsButton
                title={t("aml.displayScoresWithoutHits")}
                onClick={() => setDisplayWithoutScores(!displayWithoutScores)}
                icon={
                  displayWithoutScores ? (
                    <ChevronSmallUpIcon />
                  ) : (
                    <ChevronSmallDownIcon />
                  )
                }
              />
            </Row>
          </InfoRuleBoxescontainer>
          <DetailedContainer>
            {selectedRuleObject && (
              <ClientRiskRuleDetailedActionComponent
                rule={selectedRuleObject}
                clientIdentifier={clientIdentifer}
                riskScoreId={riskScore.current?.id || ""}
                activeExclusions={riskScore.activeExclusions}
                refetchRiskScore={refetchRiskScore}
              />
            )}
          </DetailedContainer>
        </div>
      </OutlinedDiv>
    </div>
  );
};

export default ClientRiskScores;
